import { useState, useCallback } from 'react';
import { useAppStore, ALL_PROPS, KeyframeProp, Layer } from '../store/useAppStore';
import { viewportActions } from '../viewportActions';

// Module-level lookup so it is never recreated per call
const PROP_VALUE: Record<KeyframeProp, (l: Layer) => number> = {
  opacity:    (l) => l.opacity,
  rotateX:    (l) => l.rotate.x,
  rotateY:    (l) => l.rotate.y,
  rotateZ:    (l) => l.rotate.z,
  translateX: (l) => l.translate.x,
  translateY: (l) => l.translate.y,
};

// ── Hook ──────────────────────────────────────────────────────────────────────
// Owns: layer/camera KF helpers, expanded state, toggleExpanded.
// All callbacks are stable (useCallback) and safe to pass as React props.

export function useTimelineHelpers() {
  const setKeyframe          = useAppStore((s) => s.setKeyframe);
  const removeKeyframe       = useAppStore((s) => s.removeKeyframe);
  const setCameraKeyframe    = useAppStore((s) => s.setCameraKeyframe);
  const removeCameraKeyframe = useAppStore((s) => s.removeCameraKeyframe);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const addAllKfsAtFrame = useCallback(
    (layerId: string, frame: number) => {
      const layer = useAppStore.getState().layers.find((l) => l.id === layerId);
      if (!layer) return;
      viewportActions.pushSnapshot.current();
      setKeyframe(layerId, 'opacity',    frame, layer.opacity);
      setKeyframe(layerId, 'rotateX',    frame, layer.rotate.x);
      setKeyframe(layerId, 'rotateY',    frame, layer.rotate.y);
      setKeyframe(layerId, 'rotateZ',    frame, layer.rotate.z);
      setKeyframe(layerId, 'translateX', frame, layer.translate.x);
      setKeyframe(layerId, 'translateY', frame, layer.translate.y);
    },
    [setKeyframe]
  );

  const removeAllKfsAtFrame = useCallback(
    (layerId: string, frame: number) => {
      viewportActions.pushSnapshot.current();
      ALL_PROPS.forEach((p) => removeKeyframe(layerId, p, frame));
    },
    [removeKeyframe]
  );

  const addPropKfAtFrame = useCallback(
    (layerId: string, prop: KeyframeProp, frame: number) => {
      const layer = useAppStore.getState().layers.find((l) => l.id === layerId);
      if (!layer) return;
      viewportActions.pushSnapshot.current();
      setKeyframe(layerId, prop, frame, PROP_VALUE[prop](layer));
    },
    [setKeyframe]
  );

  const hasKfAtFrame = useCallback(
    (layerId: string, frame: number) => {
      const kfs = useAppStore.getState().layerKeyframes[layerId];
      return kfs ? ALL_PROPS.some((p) => frame in kfs[p]) : false;
    },
    [],
  );

  const addCamKfAtFrame = useCallback(
    (frame: number) => {
      const { currentCamPos: pos, cameraFov } = useAppStore.getState();
      viewportActions.pushSnapshot.current();
      setCameraKeyframe(frame, { x: pos.x, y: pos.y, z: pos.z, fov: cameraFov });
    },
    [setCameraKeyframe]
  );

  const removeCamKfAtFrame = useCallback(
    (frame: number) => {
      viewportActions.pushSnapshot.current();
      removeCameraKeyframe(frame);
    },
    [removeCameraKeyframe]
  );

  return {
    expanded,
    toggleExpanded,
    addAllKfsAtFrame,
    removeAllKfsAtFrame,
    addPropKfAtFrame,
    hasKfAtFrame,
    addCamKfAtFrame,
    removeCamKfAtFrame,
    removeKeyframe, // exposed for LayerLabelRow.onRemoveKf
  };
}
